'use client';
import React, { useEffect, createContext, useRef, useState, useMemo, useContext } from "react";
import ReactDOM from "react-dom";
import { CSSTransition } from "react-transition-group";

import Button from "./Button";
import Icons from "./Icons";
import Header from "./Header";
import { generateUEID } from "../utils/helpers";

const DrawersContext = createContext(null);

export function DrawerProvider(props: any) {
  const [drawers, setdrawers] = useState([]);
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  const open = (drawerConfig: any) => {
    let _id = generateUEID();
    setdrawers((drawers) => [...drawers.map((drawer: any) => {
      return { ...drawer, active: true }
    }), { id: _id, component: drawerConfig.component, props: drawerConfig.props, active: true }]);
  }

  const close = () => {
    setdrawers((drawers: any) => {
      let _drawers = drawers.slice();
      let _currentDrawer = _drawers[_drawers.length - 1];
      _currentDrawer.active = false;
      if (_drawers.length >= 2) {
        _drawers[_drawers.length - 2].active = true;
      }
      setTimeout(() => {
        setdrawers((drawers) => drawers.filter((drawer) => drawer.id != _currentDrawer.id));
      }, 200);
      return _drawers
    })
  }

  const closeAll = () => {
    setdrawers((drawers) => drawers.map(drawer => { return { ...drawer, active: false } }));
    setTimeout(() => {
      setdrawers([]);
    }, 500);
  }

  const contextValue = useMemo(() => ({ open, closeAll, close }), []);

  return (
    <DrawersContext.Provider value={contextValue}>
      {props.children}
      {ReactDOM.createPortal(
        <div className="loop">
          <CSSTransition
            in={drawers.length > 0}
            appear={true}
            unmountOnExit={true}
            timeout={{ enter: 0, exit: 200 }}
          >
            <div className="loop-drawer-overlay"></div>
          </CSSTransition>
        </div>,
        document.body
      )}
      {mounted ? ReactDOM.createPortal(
        <div className="loop">
          {drawers.map((drawer) => (
            <drawer.component
              key={drawer.id}
              {...drawer.props}
              active={drawer.active}
              toggleActive={close}
              closeAll={closeAll}
            />
          ))}
        </div>,
        document.body
      ) : null}
    </DrawersContext.Provider>
  );
}

export const useDrawer = () => useContext(DrawersContext);

function Drawer({ handleCrossClicked = undefined, closeOne = false, ...props }) {
  const { closeAll, close } = useDrawer();

  const _handleCrossClicked = () => {
    if (handleCrossClicked) {
      handleCrossClicked();
    }
    if (closeOne) {
      close();
    } else {
      closeAll();
    }
  }

  useEffect(() => {
    if (props.active) {
      const width = document.body.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.width = `${width}px`;
    } else {
      document.body.style.overflow = "visible";
      document.body.style.width = `auto`;
    }

    return () => {
      document.body.style.overflow = "visible";
      document.body.style.width = `auto`;
    };
  }, [props.active]);

  return (
    <CSSTransition
      in={props.active}
      appear={true}
      unmountOnExit={true}
      timeout={{ enter: 0, exit: 200 }}
    >
      <div className="loop-drawer" onClick={closeAll} id={props.id ? `loop-cp-drawer-id-${props.id}` : ''}>
        <div className="loop-drawer-content" onClick={e => e.stopPropagation()} data-variant-id={props?.selectedVariantShopifyId} data-product-id={props?.product?.product_shopify_id || props?.product?.product_id} loop-is-bundle={props?.product?.isBundle} data-product-handle={props?.product?.product_handle}>
          <div className="loop-drawer-header d-flex w-100 p-3 justify-content-between flex-row-reverse">
            <Button variant="underline" onClick={_handleCrossClicked}>
              <Icons.Cancel />
            </Button>
            {props.title ? (
              <Header.H2>{props.title}</Header.H2>
            ) : null}
          </div>
          {props.children}
        </div>
      </div>
    </CSSTransition>
  );
};


function Body(props: any) {
  return <div className={"loop-drawer-body" + (props.className ? " " + props.className : "")}>{props.children}</div>
}

function ScrollableBody(props: any) {
  const bodyRef = useRef();

  const onScroll = () => {
    if (bodyRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = bodyRef.current;
      let _totalHeight = Math.ceil(scrollTop + clientHeight);
      if (_totalHeight >= scrollHeight) {
        if (props.onBottomReached) {
          props.onBottomReached();
        }
      }
    }
  };
  return <div className="loop-drawer-body" ref={bodyRef} onScroll={onScroll} id={props.id ? props.id : ''}>{props.children}</div>
}

function Footer(props: any) {
  return <div className="loop-drawer-footer">{props.children}</div>
}


export default Object.assign(Drawer, {
  Header: Header,
  Body: Body,
  ScrollableBody: ScrollableBody,
  Footer: Footer,
});
